/*
 |--------------------------------------------------------------------------
 | Address
 |
 | This module adds functionality to the Sitecore Forms Address component
 |--------------------------------------------------------------------------
 */

// Constants
const AAddressSelector = '.a-fieldset--address-js';
const AAddressHiddenAttribute = 'hidden';
const AAddressSpinnerSelector = `${AAddressSelector} .aa-spinner`;
const AAddressResultsSelectors = {
  wrapper: `${AAddressSelector} .results`,
  line1: `${AAddressSelector} .line1`,
  line2: `${AAddressSelector} .line2`,
};
const AAddressNotFoundSelector = `${AAddressSelector} .not-found`;
const AAddressInputSelectors = {
  postalcode: `${AAddressSelector} #address-postalcode input`,
  number: `${AAddressSelector} #address-number input`,
  addition: `${AAddressSelector} #address-addition input`,
  street: `${AAddressSelector} #address-street input`,
  city: `${AAddressSelector} #address-city input`,
};
const AAddressApiPath = '/apiproxy/achmea-adressen-v1-zoekenAdres';
const AAddressStates = {
  initial: 'initial',
  loading: 'loading',
  found: 'found',
  notFound: 'notFound',
};

// Class
class AAddress {
  postalcode;
  number;
  addition;
  street;
  streetField;
  city;
  cityField;
  prefilledFieldset;
  spinner;
  results;
  resultLine1;
  resultsLine2;
  notFoundMessage;

  constructor(element) {
    this.element = element;
  }

  init = () => {
    // Get all elements
    this.postalcode = this.element.querySelector(
      AAddressInputSelectors.postalcode
    );
    this.number = this.element.querySelector(AAddressInputSelectors.number);
    this.addition = this.element.querySelector(AAddressInputSelectors.addition);
    this.street = this.element.querySelector(AAddressInputSelectors.street);
    this.streetField = this.street.closest('.a-field');
    this.city = this.element.querySelector(AAddressInputSelectors.city);
    this.cityField = this.city.closest('.a-field');
    this.spinner = this.element.querySelector(AAddressSpinnerSelector);
    this.results = this.element.querySelector(AAddressResultsSelectors.wrapper);
    this.resultLine1 = this.element.querySelector(
      AAddressResultsSelectors.line1
    );
    this.resultsLine2 = this.element.querySelector(
      AAddressResultsSelectors.line2
    );
    this.notFoundMessage = this.element.querySelector(AAddressNotFoundSelector);

    this.setState(AAddressStates.initial);
    // If fields are already filled don't manipulate them
    if (
      (this.street.value != null && this.street.value != '') ||
      (this.number.value != null && this.number.value != '')
    ) {
      this.getAddress(
        this.postalcode.value,
        this.number.value,
        this.addition ? this.addition.value : null
      );
    }

    // Input Listeners
    this.postalcode.addEventListener(
      'input',
      this.debounce(() => this.onPostalcodeInput())
    );
    this.number.addEventListener(
      'input',
      this.debounce(() => this.onInput())
    );
    this.addition.addEventListener(
      'input',
      this.debounce(() => this.onInput())
    );
  };

  debounce = (callback, timeout = 300) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        callback.apply(this, args);
      }, timeout);
    };
  };

  onPostalcodeInput = () => {
    if (!this.postalcode) return;

    this.postalcode.value = this.postalcode.value
      .trim()
      .replace(/\s/g, '')
      .toUpperCase();

    this.onInput();
  };

  onInput = () => {
    if (!this.postalcode || !this.number) return;

    if (this.postalcode.value !== '' && this.number.value !== '') {
      this.street.value = '';
      this.city.value = '';

      this.getAddress(
        this.postalcode.value,
        this.number.value,
        this.addition ? this.addition.value : null
      );
    }
  };

  getAddress = async (postalcode, number, addition) => {
    if (!postalcode || !number) return;

    const token = document.querySelector(
      'input[name="__RequestVerificationToken"]'
    )?.value;
    if (!token) return;

    this.setState(AAddressStates.loading);

    if (isNaN(number)) {
      this.setState(AAddressStates.notFound);
      return;
    }

    const data = {
      adres: {
        huisnummer: number,
        huisnummerToevoeging: addition,
        postcode: postalcode,
      },
    };

    // Mock
    if (window.location.origin.includes('localhost')) {
      const mock = await new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            adres: {
              straat: 'Laan van Malkenschoten',
              huisnummer: '20',
              huisnummerToevoeging: 'A',
              postcode: '7333 NP',
              plaats: 'APELDOORN',
            },
            postbusGevonden: false,
            adresGevonden: true,
          });
        }, 1000);
      });
      this.handleResult(mock);
      return;
    }

    try {
      const response = await fetch(
        `${window.location.origin}/${AAddressApiPath}`,
        {
          method: 'POST',
          headers: {
            __RequestVerificationToken: token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        }
      );
      const result = await response.json();
      this.handleResult(result);
    } catch (error) {
      this.handleResult(null);
    }
  };

  handleResult = (result) => {
    if (result && result.adresGevonden) {
      this.resultLine1.innerText = `${result.adres.straat} ${
        result.adres.huisnummer
      }${
        result.adres.huisnummerToevoeging
          ? ` ${result.adres.huisnummerToevoeging}`
          : ''
      }`;
      this.resultsLine2.innerText = `${result.adres.postcode} ${result.adres.plaats}`;
      this.street.value = result.adres.straat;
      this.city.value = result.adres.plaats;
      this.setState(AAddressStates.found);
    } else {
      this.setState(AAddressStates.notFound);
    }
  };

  setState = (state) => {
    switch (state) {
      case AAddressStates.initial:
        this.hide(this.streetField);
        this.hide(this.cityField);
        this.loading(false);
        this.hide(this.notFoundMessage);
        this.hide(this.results);
        break;

      case AAddressStates.loading:
        this.hide(this.streetField);
        this.hide(this.cityField);
        this.loading(true);
        this.hide(this.notFoundMessage);
        this.hide(this.results);
        break;

      case AAddressStates.notFound:
        this.show(this.streetField);
        this.show(this.cityField);
        this.loading(false);
        this.show(this.notFoundMessage);
        this.hide(this.results);
        break;

      case AAddressStates.found:
        this.hide(this.streetField);
        this.hide(this.cityField);
        this.loading(false);
        this.hide(this.notFoundMessage);
        this.show(this.results);
        break;

      default:
        this.show(this.streetField);
        this.show(this.cityField);
        this.loading(false);
        this.hide(this.notFoundMessage);
        this.hide(this.results);
        break;
    }
  };

  loading = (value) => {
    if (value) {
      this.show(this.spinner);
    } else {
      this.hide(this.spinner);
    }
  };
  hide = (element) => {
    if (!element.hasAttribute(AAddressHiddenAttribute)) {
      element.setAttribute(AAddressHiddenAttribute, '');
    }
  };
  show = (element) => element.removeAttribute(AAddressHiddenAttribute);
}

// Initiate module
function AAddressCreateInstance(element) {
  if (!element.addressClass) {
    element.addressClass = new AAddress(element);
    element.addressClass.init();
  }
}

// Create new instances on DOM mutation
document
  .querySelectorAll(`${AAddressSelector}`)
  .forEach(AAddressCreateInstance);

const AAddressObserver = new MutationObserver(function (mutations) {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((element) => {
      document
        .querySelectorAll(`${AAddressSelector}`)
        .forEach(AAddressCreateInstance);
    });
  });
});

AAddressObserver.observe(document, {
  attributes: false,
  childList: true,
  characterData: false,
  subtree: true,
});
